export interface AuthRedirectEnvironment {
  API_BASE_URL: string;
  APP_BASE_URL: string;
  ADMIN_BASE_URL: string;
}

function exactOrigin(raw: string): string | null {
  try {
    const url = new URL(raw);
    return url.username || url.password ? null : url.origin;
  } catch {
    return null;
  }
}

/**
 * Resolve a browser-controlled post-auth destination against a deliberately
 * small first-party allowlist. The Account and Admin SPAs may receive any path
 * on their own origins. The Account API is allowed only for World's signed
 * handoff entry point.
 */
export function safeAuthRedirect(
  raw: string | null | undefined,
  env: AuthRedirectEnvironment,
): string {
  const fallback = new URL('/', env.APP_BASE_URL).toString();
  if (!raw || (!raw.startsWith('/') && !/^https?:\/\//i.test(raw))) {
    return fallback;
  }
  if (raw.startsWith('//') || raw.includes('\\')) return fallback;

  try {
    const candidate = new URL(raw, fallback);
    if (
      candidate.protocol !== 'https:' &&
      !(candidate.protocol === 'http:' && candidate.hostname.endsWith('.local'))
    ) {
      return fallback;
    }
    if (candidate.username || candidate.password) return fallback;

    const appOrigin = exactOrigin(env.APP_BASE_URL);
    const adminOrigin = exactOrigin(env.ADMIN_BASE_URL);
    const apiOrigin = exactOrigin(env.API_BASE_URL);
    if (candidate.origin === appOrigin || candidate.origin === adminOrigin) {
      return candidate.toString();
    }
    if (
      candidate.origin === apiOrigin &&
      candidate.pathname === '/world/auth/start'
    ) {
      return candidate.toString();
    }
  } catch {
    // Malformed destinations fail back to the Account root.
  }
  return fallback;
}

/**
 * Complete every Account login through the signed World handoff unless the
 * caller already requested that handoff. This mints the World-side session
 * carrier as part of the same login, so an Account login is immediately
 * visible on World and Docs as well.
 */
export function sharedAuthRedirect(
  raw: string | null | undefined,
  env: AuthRedirectEnvironment,
): string {
  const target = safeAuthRedirect(raw, env);
  const apiOrigin = exactOrigin(env.API_BASE_URL);
  try {
    const targetUrl = new URL(target);
    if (
      targetUrl.origin === apiOrigin &&
      targetUrl.pathname === '/world/auth/start'
    ) {
      return targetUrl.toString();
    }

    const handoff = new URL('/world/auth/start', env.API_BASE_URL);
    handoff.searchParams.set('return_to', targetUrl.toString());
    return handoff.toString();
  } catch {
    return target;
  }
}
